import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import {
    ArrowLeft, Phone, MessageCircle, Mail, Save, Trash2, Clock, X,
    User, CheckCircle2, AlertCircle, FileText, MessageSquarePlus,
} from "lucide-react";

import AdminLayout from "@/components/admin/AdminLayout";
import { Badge } from "./AdminDashboard";
import {
    leadDetail, patchLead, patchStatus, loseLead, addNote, deleteLead, lossReasons,
    SITIO_BADGE, TEMP_BADGE, STATUS_OPTIONS,
} from "@/lib/admin-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const ACTIVITY_ICONS = {
    lead_created: User, status_changed: CheckCircle2, note_added: MessageSquarePlus,
    catalog_downloaded: FileText, lead_enriched: FileText, lead_lost: X, email_sent: Mail,
};

export default function AdminLeadDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState(null);
    const [editing, setEditing] = useState({});
    const [saving, setSaving] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [reasons, setReasons] = useState([]);
    const [loseModal, setLoseModal] = useState(false);
    const [loseReason, setLoseReason] = useState("");
    const [loseNote, setLoseNote] = useState("");

    const user = JSON.parse(localStorage.getItem("admin_user") || "{}");
    const isSuperAdmin = user.role === "super_admin";

    const load = () => leadDetail(id).then((d) => { setDetail(d); setEditing(d.lead); }).catch(() => toast.error("Lead no encontrado"));
    useEffect(() => { load(); }, [id]);
    useEffect(() => { lossReasons().then(setReasons); }, []);

    if (!detail) return <AdminLayout title="Lead"><div className="text-eclipse-muted">Cargando...</div></AdminLayout>;

    const { lead, notes, activity } = detail;
    const isLost = lead.estatus === "perdido";
    const sitio = SITIO_BADGE[lead.sitio] || SITIO_BADGE.eclipse;
    const temp = TEMP_BADGE[lead.lead_temp];
    const st = STATUS_OPTIONS.find((s) => s.value === lead.estatus);

    const onField = (k) => (e) => setEditing({ ...editing, [k]: typeof e === "object" && e?.target ? e.target.value : e });

    const onSave = async () => {
        setSaving(true);
        try {
            const fields = ["nombre", "empresa", "cargo", "telefono", "email", "tipo_espacio", "metros_cuadrados", "ciudad", "mensaje", "presupuesto_estimado", "responsable", "next_followup_at", "last_contact_at"];
            const body = Object.fromEntries(fields.filter((k) => editing[k] !== lead[k]).map((k) => [k, editing[k]]));
            if (Object.keys(body).length === 0) { toast.info("Sin cambios"); return; }
            await patchLead(id, body);
            toast.success("Guardado");
            load();
        } catch { toast.error("Error al guardar"); }
        finally { setSaving(false); }
    };

    const onStatusChange = async (estatus) => {
        if (estatus === "perdido") { setLoseModal(true); return; }
        try { await patchStatus(id, estatus); toast.success("Estatus actualizado"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    };

    const onLose = async () => {
        if (!loseReason) return toast.error("Selecciona motivo");
        try { await loseLead(id, loseReason, loseNote || null); toast.success("Marcado como perdido"); setLoseModal(false); load(); }
        catch { toast.error("Error"); }
    };

    const onAddNote = async () => {
        if (!newNote.trim()) return;
        try { await addNote(id, newNote); setNewNote(""); load(); toast.success("Nota agregada"); }
        catch { toast.error("Error"); }
    };

    const onDelete = async () => {
        if (!window.confirm("¿Eliminar este lead permanentemente?")) return;
        try { await deleteLead(id); toast.success("Lead eliminado"); navigate("/admin/leads"); }
        catch { toast.error("Error al eliminar"); }
    };

    const telDigits = (lead.telefono || "").replace(/\D/g, "");

    return (
        <AdminLayout title="Detalle de lead">
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-sm text-eclipse-muted hover:text-eclipse-cta mb-4" data-testid="lead-detail-back">
                <ArrowLeft className="w-4 h-4" /> Volver a leads
            </Link>

            {isLost && (
                <div className="bg-red-50 border border-red-200 rounded-sm p-4 mb-5">
                    <p className="font-display font-bold text-red-900">Lead perdido — {lead.loss_reason}</p>
                    {lead.loss_reason_note && <p className="text-sm text-red-700 mt-1">{lead.loss_reason_note}</p>}
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h2 className="font-display font-black text-3xl text-eclipse-primary" data-testid="lead-detail-name">{lead.nombre}</h2>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                        <Badge {...sitio} />
                        <Badge {...temp} bordered />
                        <span className="font-display font-black text-eclipse-cta text-lg">Score {lead.lead_score}</span>
                        <Badge label={st?.label || lead.estatus} color={st?.color} bordered />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {telDigits && (
                        <>
                            <a href={`https://wa.me/${telDigits}`} target="_blank" rel="noopener noreferrer" data-testid="lead-wa-btn" className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#1ebd5b] text-white text-sm font-display font-bold uppercase rounded-sm">
                                <MessageCircle className="w-4 h-4" /> WhatsApp
                            </a>
                            <a href={`tel:${telDigits}`} data-testid="lead-call-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-eclipse-primary hover:border-eclipse-cta hover:text-eclipse-cta text-sm font-display font-bold uppercase rounded-sm">
                                <Phone className="w-4 h-4" /> Llamar
                            </a>
                        </>
                    )}
                    {lead.email && (
                        <a href={`mailto:${lead.email}`} data-testid="lead-email-btn" className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-eclipse-primary hover:border-eclipse-cta hover:text-eclipse-cta text-sm font-display font-bold uppercase rounded-sm">
                            <Mail className="w-4 h-4" /> Email
                        </a>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <Card title="Datos del lead">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Nombre"><Input value={editing.nombre || ""} onChange={onField("nombre")} data-testid="detail-nombre" /></Field>
                            <Field label="Empresa"><Input value={editing.empresa || ""} onChange={onField("empresa")} data-testid="detail-empresa" /></Field>
                            <Field label="Cargo"><Input value={editing.cargo || ""} onChange={onField("cargo")} data-testid="detail-cargo" /></Field>
                            <Field label="Teléfono"><Input value={editing.telefono || ""} onChange={onField("telefono")} data-testid="detail-telefono" /></Field>
                            <Field label="Email"><Input type="email" value={editing.email || ""} onChange={onField("email")} data-testid="detail-email" /></Field>
                            <Field label="Tipo de espacio"><Input value={editing.tipo_espacio || ""} onChange={onField("tipo_espacio")} /></Field>
                            <Field label="Metros²"><Input value={editing.metros_cuadrados || ""} onChange={onField("metros_cuadrados")} /></Field>
                            <Field label="Ciudad"><Input value={editing.ciudad || ""} onChange={onField("ciudad")} /></Field>
                            <Field label="Presupuesto"><Input type="number" value={editing.presupuesto_estimado || ""} onChange={onField("presupuesto_estimado")} /></Field>
                            <Field label="Responsable"><Input value={editing.responsable || ""} onChange={onField("responsable")} placeholder="Carlos Méndez" /></Field>
                            <Field label="Próximo follow-up"><Input type="datetime-local" value={editing.next_followup_at ? editing.next_followup_at.slice(0, 16) : ""} onChange={(e) => onField("next_followup_at")(e.target.value ? e.target.value + ":00" : null)} data-testid="detail-followup" /></Field>
                            <Field label="Último contacto"><Input type="datetime-local" value={editing.last_contact_at ? editing.last_contact_at.slice(0, 16) : ""} onChange={(e) => onField("last_contact_at")(e.target.value ? e.target.value + ":00" : null)} /></Field>
                            <div className="md:col-span-2"><Field label="Mensaje"><Textarea value={editing.mensaje || ""} onChange={onField("mensaje")} rows={3} /></Field></div>
                        </div>
                        <div className="mt-4 flex justify-end">
                            <Button onClick={onSave} disabled={saving} className="btn-eclipse-cta px-5 py-2 text-xs uppercase tracking-wider rounded-sm" data-testid="detail-save-btn">
                                <Save className="w-3.5 h-3.5 mr-2" /> {saving ? "Guardando..." : "Guardar"}
                            </Button>
                        </div>
                    </Card>

                    <Card title="UTMs y origen">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                            <Row k="UTM Source" v={lead.utm_source} />
                            <Row k="UTM Medium" v={lead.utm_medium} />
                            <Row k="UTM Campaign" v={lead.utm_campaign} />
                            <Row k="UTM Term" v={lead.utm_term} />
                            <Row k="UTM Content" v={lead.utm_content} />
                            <Row k="Referrer" v={lead.referrer} />
                            <Row k="Página" v={lead.pagina} />
                            <Row k="IP" v={lead.client_ip} />
                            <Row k="Fuente" v={lead.fuente} />
                            <Row k="Producto" v={lead.producto} />
                        </dl>
                    </Card>

                    <Card title="Notas">
                        <div className="flex gap-2 mb-4">
                            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Escribe una nota interna..." rows={2} data-testid="detail-note-input" />
                            <Button onClick={onAddNote} className="btn-eclipse-cta self-end px-4 py-2 rounded-sm" data-testid="detail-add-note-btn">Agregar</Button>
                        </div>
                        {notes.length === 0 ? <p className="text-sm text-eclipse-muted">Sin notas aún</p> : (
                            <ul className="space-y-2">
                                {notes.map((n) => (
                                    <li key={n.id} className="bg-neutral-50 p-3 rounded-sm" data-testid={`note-${n.id}`}>
                                        <p className="text-sm text-eclipse-primary">{n.nota}</p>
                                        <p className="text-[10px] text-eclipse-muted mt-1.5">{n.by} · {format(parseISO(n.created_at), "d MMM yyyy HH:mm", { locale: es })}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Card>
                </div>

                <div className="space-y-4">
                    <Card title="Cambiar estatus">
                        <Select value={lead.estatus} onValueChange={onStatusChange}>
                            <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                    </Card>

                    <Card title="Score">
                        <div className="text-center py-2">
                            <div className="font-display font-black text-5xl text-eclipse-cta mb-1">{lead.lead_score}</div>
                            <Badge {...temp} bordered />
                            <p className="text-xs text-eclipse-muted mt-3 leading-relaxed">Calculado con fuente, producto, m², planos, tipo de espacio, UTMs y estatus.</p>
                        </div>
                    </Card>

                    <Card title="Actividad">
                        {activity.length === 0 ? <p className="text-sm text-eclipse-muted">Sin actividad</p> : (
                            <ul className="space-y-3">
                                {activity.map((a) => {
                                    const I = ACTIVITY_ICONS[a.type] || Clock;
                                    return (
                                        <li key={a.id} className="flex gap-3 text-xs">
                                            <I className="w-4 h-4 text-eclipse-cta flex-shrink-0 mt-0.5" />
                                            <div>
                                                <p className="text-eclipse-primary">{a.description}</p>
                                                <p className="text-[10px] text-eclipse-muted">{format(parseISO(a.created_at), "d MMM HH:mm", { locale: es })} · {a.by}</p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </Card>

                    <Card title="Correos">
                        <p className="text-sm text-eclipse-muted flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> Historial disponible en Fase 4 (Resend)
                        </p>
                    </Card>

                    {isSuperAdmin && (
                        <Button onClick={onDelete} variant="outline" className="w-full text-red-600 border-red-300 hover:bg-red-50" data-testid="detail-delete-btn">
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar lead
                        </Button>
                    )}
                </div>
            </div>

            <Dialog open={loseModal} onOpenChange={setLoseModal}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><X className="w-5 h-5 text-red-600" /> Marcar como perdido</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Field label="Motivo *">
                            <Select value={loseReason} onValueChange={setLoseReason}>
                                <SelectTrigger data-testid="detail-lose-reason"><SelectValue placeholder="Selecciona motivo" /></SelectTrigger>
                                <SelectContent>{reasons.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Nota interna (opcional)">
                            <Textarea value={loseNote} onChange={(e) => setLoseNote(e.target.value)} rows={2} />
                        </Field>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setLoseModal(false)}>Cancelar</Button>
                        <Button onClick={onLose} data-testid="detail-lose-confirm" className="bg-red-600 hover:bg-red-700 text-white">Confirmar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AdminLayout>
    );
}

function Card({ title, children }) {
    return (
        <section className="bg-white border border-neutral-200 rounded-sm p-5">
            <h3 className="font-display font-bold text-xs uppercase tracking-wider text-eclipse-primary mb-4">{title}</h3>
            {children}
        </section>
    );
}
function Field({ label, children }) {
    return (
        <div>
            <Label className="text-[10px] font-display font-semibold text-eclipse-muted tracking-wider uppercase mb-1 block">{label}</Label>
            {children}
        </div>
    );
}
function Row({ k, v }) {
    return (
        <>
            <dt className="text-xs text-eclipse-muted py-1">{k}</dt>
            <dd className="text-xs text-eclipse-primary py-1 truncate">{v || "—"}</dd>
        </>
    );
}
